import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
} from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";
import ServiceItemDialog from "./ServiceItemDialog";

const server = EnvSettings.server;

const sortServiceItems = (items = []) => {
  return [...items].sort((a, b) => {
    const skuA = (a?.sku || "").toLowerCase();
    const skuB = (b?.sku || "").toLowerCase();
    if (skuA < skuB) {
      return -1;
    }
    if (skuA > skuB) {
      return 1;
    }
    return 0;
  });
};

export default function ServiceItemModule() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [serviceItems, setServiceItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeServiceItem, setActiveServiceItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchServiceItems = useCallback(() => {
    setIsLoading(true);
    fetch(server + "/get_service_items", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        limit: 500,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setServiceItems(sortServiceItems(result.serviceItems || []));
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t("serviceItemsModule.loadFailed"),
          }),
        );
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, t]);

  useEffect(() => {
    fetchServiceItems();
  }, [fetchServiceItems]);

  const filteredItems = useMemo(() => {
    if (!searchValue.trim()) {
      return serviceItems;
    }
    const normalized = searchValue.trim().toLowerCase();
    return serviceItems.filter((item) => {
      const values = [
        item?.sku,
        item?.name,
        item?.imageUrl,
        item?.id ? String(item.id) : "",
        item?.providerInfo?.name,
        item?.providerInfo?.email,
        item?.providerInfo?.phone,
      ];
      return values.some((field) =>
        field ? field.toLowerCase().includes(normalized) : false,
      );
    });
  }, [serviceItems, searchValue]);

  const handleRowClick = (item) => {
    setActiveServiceItem(item);
    setIsDialogOpen(true);
  };

  const handleAddClick = () => {
    setActiveServiceItem(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (isSubmitting) {
      return;
    }
    setIsDialogOpen(false);
    setActiveServiceItem(null);
  };

  const handleCreate = (payload) => {
    setIsSubmitting(true);
    fetch(server + "/add_service_item", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        serviceItem: payload,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setServiceItems((prev) =>
            sortServiceItems([...(prev || []), result.serviceItem]),
          );
          dispatch(
            showPopupSnackbar({
              error: false,
              message: t("serviceItemsModule.serviceItemCreated"),
            }),
          );
          setIsDialogOpen(false);
          setActiveServiceItem(null);
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t("serviceItemsModule.createFailed"),
          }),
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleUpdate = (payload) => {
    if (!activeServiceItem) {
      return;
    }
    setIsSubmitting(true);
    fetch(server + "/edit_service_item", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        serviceItemId: activeServiceItem.id,
        serviceItem: payload,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setServiceItems((prev) =>
            sortServiceItems(
              (prev || []).map((item) =>
                item.id === activeServiceItem.id ? result.serviceItem : item,
              ),
            ),
          );
          dispatch(
            showPopupSnackbar({
              error: false,
              message: t("serviceItemsModule.serviceItemUpdated"),
            }),
          );
          setIsDialogOpen(false);
          setActiveServiceItem(null);
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t("serviceItemsModule.updateFailed"),
          }),
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleDialogSubmit = (values) => {
    const payload = {
      ...values,
      name: values.name ? values.name.trim() : "",
      imageUrl: values.imageUrl ? values.imageUrl.trim() : "",
    };
    if (activeServiceItem) {
      handleUpdate(payload);
    } else {
      handleCreate(payload);
    }
  };

  const handleDelete = () => {
    if (!activeServiceItem) {
      return;
    }
    setIsSubmitting(true);
    fetch(server + "/delete_service_item", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        serviceItemId: activeServiceItem.id,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setServiceItems((prev) =>
            (prev || []).filter((item) => item.id !== activeServiceItem.id),
          );
          dispatch(
            showPopupSnackbar({
              error: false,
              message: t("serviceItemsModule.serviceItemDeleted"),
            }),
          );
          setIsDialogOpen(false);
          setActiveServiceItem(null);
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t("serviceItemsModule.deleteFailed"),
          }),
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper className={classes.paper} style={{ marginBottom: 10 }}>
          <Grid container spacing={3}>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                onClick={handleAddClick}
              >
                + {t("serviceItemsModule.addServiceItem")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>{t("serviceItemsModule.header")}</Title>
          <TextField
            label={t("serviceItemsModule.searchPlaceholder")}
            variant="outlined"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            fullWidth
          />
          <Grid
            item
            xs={12}
            style={{
              borderTop: "1px solid rgb(203,203,203)",
              marginTop: 20,
              marginBottom: 50,
            }}
          >
            {isLoading ? (
              <Grid container justify="center" style={{ padding: "32px 0" }}>
                <CircularProgress />
              </Grid>
            ) : (
              <SortableTable
                downloadButton={true}
                tableData={filteredItems}
                headCells={[
                  {
                    id: "sku",
                    numeric: false,
                    disablePadding: false,
                    label: t("serviceItemsModule.sku"),
                  },
                  {
                    id: "name",
                    numeric: false,
                    disablePadding: false,
                    label: t("serviceItemsModule.name"),
                  },
                  {
                    id: "costPrice",
                    numeric: false,
                    disablePadding: false,
                    label: t("serviceItemsModule.costPrice"),
                  },
                  {
                    id: "listPrice",
                    numeric: false,
                    disablePadding: false,
                    label: t("serviceItemsModule.listPrice"),
                  },
                  {
                    id: "providerInfo",
                    numeric: false,
                    disablePadding: false,
                    label: t("serviceItemsModule.providerInfoLabel"),
                  },
                  {
                    id: "updatedAt",
                    numeric: false,
                    disablePadding: false,
                    label: t("serviceItemsModule.updatedAt"),
                  },
                ]}
                defaultSortRow="sku"
                handleRowClick={handleRowClick}
                rowsPerPage={25}
                specialDataFormatting={[
                  {
                    id: "updatedAt",
                    format: (value) =>
                      value ? formatTimeWithTZOffset(value, ECABIN) : "",
                  },
                  {
                    id: "providerInfo",
                    format: (value, _img, row) => {
                      const info = value || row?.providerInfo;
                      if (!info) {
                        return "";
                      }
                      return [info.name, info.email, info.phone]
                        .filter(Boolean)
                        .join(" · ");
                    },
                  },
                ]}
              />
            )}
          </Grid>
        </Paper>
      </Grid>

      <ServiceItemDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        serviceItem={activeServiceItem}
        onSubmit={handleDialogSubmit}
        onDelete={handleDelete}
        isSubmitting={isSubmitting}
      />
    </Grid>
  );
}
